/**
 * batch-62 telefoonabonnementen (FO-463) - telefoonnummer-normalisatie.
 *
 * DE matchsleutel van het telefonie-register: een telefoonnummer genormaliseerd
 * naar E.164 (`+31612345678`). Het register (`Abonnement.nummerE164`), de import
 * (dedupe) en de providerfacturen (Vodafone/Odido: regel -> abonnement) gebruiken
 * allemaal deze ene helper, zodat "06-12345678", "+31 (0)6 1234 5678",
 * "0031612345678" en het Excel-getal 612345678 dezelfde sleutel opleveren.
 *
 * Puur en zonder dependencies (bewust geen libphonenumber: we hebben alleen de
 * Nederlandse nummerplannen + een generieke E.164-controle voor buitenlandse
 * nummers nodig). Ongeldig of ambigu (twee nummers in één cel) -> `null`, nooit
 * een gok.
 *
 * ICCID (SIM-nummer) is de tweede sleutel: `normaliseerIccid`.
 */

export type TelefoonnummerSoort = 'MOBIEL' | 'VAST' | 'SERVICE' | 'M2M' | 'BUITENLAND'

export interface GenormaliseerdTelefoonnummer {
  /** E.164, bv. `+31612345678`. */
  e164: string
  soort: TelefoonnummerSoort
  /** Landcode zonder plus (`31`); `null` bij een buitenlands nummer (niet opgesplitst). */
  landcode: string | null
  /** Nederlands significant nummer zonder trunk-0 (`612345678`); `null` bij buitenland. */
  nationaal: string | null
}

const NL = '31'

/**
 * Kengetallen met twee cijfers (na de trunk-0): 010, 020, 030, … plus de niet-
 * geografische 084/085/087/088/091. Alle andere vaste nummers hebben een kengetal
 * van drie cijfers (0513, 0229, …). Alleen voor de leesbare weergave.
 */
const TWEECIJFER_KENGETALLEN = new Set([
  '10', '13', '15', '20', '23', '24', '26', '30', '33', '35', '36', '38', '40', '43', '45', '46',
  '50', '53', '55', '58', '70', '71', '72', '73', '74', '75', '76', '77', '78', '79',
  '84', '85', '87', '88', '91',
])

/** Classificeer een Nederlands significant nummer (zonder trunk-0); `null` = ongeldig. */
function classificeerNl(nationaal: string): TelefoonnummerSoort | null {
  if (/^6\d{8}$/.test(nationaal)) return 'MOBIEL'
  // M2M (machine-to-machine, trackers/data-SIM's): 097 + 9 cijfers.
  if (/^97\d{9}$/.test(nationaal)) return 'M2M'
  if (/^(800|900|906|909)(\d{4}|\d{7})$/.test(nationaal)) return 'SERVICE'
  if (/^[1-9]\d{8}$/.test(nationaal)) return 'VAST'
  return null
}

function nlNummer(nationaalRuw: string): GenormaliseerdTelefoonnummer | null {
  // Een tweede trunk-0 achter de landcode ("+31 06…", "0031 06…") is een veel-
  // gemaakte schrijffout; die mag weg.
  const nationaal = nationaalRuw.startsWith('0') ? nationaalRuw.slice(1) : nationaalRuw
  const soort = classificeerNl(nationaal)
  if (!soort) return null
  return { e164: `+${NL}${nationaal}`, soort, landcode: NL, nationaal }
}

function internationaal(cijfers: string): GenormaliseerdTelefoonnummer | null {
  if (cijfers.startsWith(NL)) return nlNummer(cijfers.slice(NL.length))
  // E.164: maximaal 15 cijfers; korter dan 8 is geen bruikbaar abonneenummer.
  if (cijfers.length < 8 || cijfers.length > 15) return null
  if (cijfers.startsWith('0')) return null
  return { e164: `+${cijfers}`, soort: 'BUITENLAND', landcode: null, nationaal: null }
}

/**
 * Normaliseer een telefoonnummer (elk NL-formaat, E.164, `00`-prefix, `tel:`-URI
 * of een Excel-getal zonder voorloopnul) naar E.164.
 *
 * @returns het genormaliseerde nummer, of `null` als het ongeldig of ambigu is.
 */
export function normaliseerTelefoonnummer(invoer: unknown): GenormaliseerdTelefoonnummer | null {
  if (invoer === null || invoer === undefined) return null
  let s: string
  if (typeof invoer === 'number') {
    // Excel-cel als getal: de voorloopnul is weg (612345678). Alleen hele,
    // positieve, veilige getallen; een wetenschappelijke notatie is al verminkt.
    if (!Number.isSafeInteger(invoer) || invoer <= 0) return null
    s = String(invoer)
  } else if (typeof invoer === 'bigint') {
    s = invoer.toString()
  } else if (typeof invoer === 'string') {
    s = invoer
  } else {
    return null
  }

  s = s.trim().replace(/^tel:/i, '')
  if (!s) return null
  // "+31 (0)6 1234 5678": de trunk-0 tussen haakjes hoort niet bij E.164.
  s = s.replace(/\(\s*0\s*\)/g, '')
  const metPlus = s.startsWith('+')
  const cijfers = s.replace(/\D/g, '')
  if (!cijfers) return null

  if (metPlus) return internationaal(cijfers)
  if (cijfers.startsWith('00')) return internationaal(cijfers.slice(2))
  if (cijfers.startsWith('0')) return nlNummer(cijfers.slice(1))
  // "31612345678" (CSV/Excel zonder plus) of M2M "3197…".
  if (cijfers.startsWith(NL) && (cijfers.length === 11 || cijfers.length === 13)) {
    return nlNummer(cijfers.slice(NL.length))
  }
  // Excel at de voorloopnul op: "612345678" (9 cijfers) of M2M "97…" (11 cijfers).
  if (cijfers.length === 9 || (cijfers.length === 11 && cijfers.startsWith('97'))) {
    return nlNummer(cijfers)
  }
  return null
}

/** Alleen de E.164-sleutel (of `null`). Handig als index/matchsleutel. */
export function telefoonnummerSleutel(invoer: unknown): string | null {
  return normaliseerTelefoonnummer(invoer)?.e164 ?? null
}

/** Zijn twee invoeren hetzelfde telefoonnummer (na normalisatie)? */
export function zijnZelfdeTelefoonnummer(a: unknown, b: unknown): boolean {
  const ka = telefoonnummerSleutel(a)
  return ka !== null && ka === telefoonnummerSleutel(b)
}

/**
 * Leesbare weergave: `06-12345678`, `020-1234567`, `0513-123456`, `0800-1234`,
 * `0970-12345678`; buitenlandse nummers in E.164. Onleesbare invoer komt
 * ongewijzigd terug (nooit een verzonnen nummer); leeg -> `null`.
 */
export function formatTelefoonnummer(invoer: string | null | undefined): string | null {
  if (!invoer) return null
  const n = normaliseerTelefoonnummer(invoer)
  if (!n) return invoer
  if (n.landcode !== NL || !n.nationaal) return n.e164
  const nat = n.nationaal
  switch (n.soort) {
    case 'MOBIEL':
      return `06-${nat.slice(1)}`
    case 'SERVICE':
    case 'M2M':
      return `0${nat.slice(0, 3)}-${nat.slice(3)}`
    case 'VAST': {
      const lengte = TWEECIJFER_KENGETALLEN.has(nat.slice(0, 2)) ? 2 : 3
      return `0${nat.slice(0, lengte)}-${nat.slice(lengte)}`
    }
    default:
      return n.e164
  }
}

// ─── ICCID ───────────────────────────────────────────────────────────────────

/**
 * Normaliseer een ICCID (het SIM-nummer, 18-22 cijfers, begint met `89`). Spaties,
 * streepjes en een afsluitende opvul-`F` vallen weg. Een Excel-getal boven 2^53 is
 * al onherstelbaar afgerond en wordt dus geweigerd (`null`), nooit "gerepareerd".
 */
export function normaliseerIccid(invoer: unknown): string | null {
  if (invoer === null || invoer === undefined) return null
  let s: string
  if (typeof invoer === 'number') {
    if (!Number.isSafeInteger(invoer) || invoer <= 0) return null
    s = String(invoer)
  } else if (typeof invoer === 'bigint') {
    s = invoer.toString()
  } else if (typeof invoer === 'string') {
    s = invoer
  } else {
    return null
  }
  s = s.trim().toUpperCase().replace(/[\s.-]/g, '')
  if (s.endsWith('F')) s = s.slice(0, -1)
  if (!/^\d{18,22}$/.test(s)) return null
  if (!s.startsWith('89')) return null
  return s
}

/** ICCID in groepjes van vier cijfers voor de weergave. */
export function formatIccid(iccid: string | null | undefined): string | null {
  if (!iccid) return null
  return iccid.replace(/(\d{4})(?=\d)/g, '$1 ')
}
